import * as THREE from 'three';

const TRAUMA_MAX = 1;
const TRAUMA_DECAY = 1.4;
const MAX_OFFSET = 0.55;
const MAX_ROLL = 0.08;

function pseudoNoise(t: number, seed: number): number {
  const x = Math.sin(t * 12.9898 + seed * 78.233) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}

// Chase camera: sits behind boat, pulls back with speed, leads into turns,
// FOV punch on boost, trauma-based shake.
export class CameraRig {
  private trauma = 0;
  private shakeTime = 0;
  private fovPunch = 0;
  private readonly baseFov: number;
  private readonly desired = new THREE.Vector3();
  private readonly lookAtTarget = new THREE.Vector3();
  private readonly smoothLook = new THREE.Vector3();

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly baseOffset = new THREE.Vector3(0, 3.2, 7.5),
    baseFov = 52,
  ) {
    this.baseFov = baseFov;
    this.camera.fov = baseFov;
    this.camera.updateProjectionMatrix();
  }

  addTrauma(amount: number): void {
    this.trauma = Math.min(TRAUMA_MAX, this.trauma + amount);
  }

  punchFov(degrees: number): void {
    this.fovPunch = Math.min(12, this.fovPunch + degrees);
  }

  snapTo(target: THREE.Vector3, heading: number): void {
    const back = new THREE.Vector3(Math.sin(heading), 0, Math.cos(heading)).multiplyScalar(this.baseOffset.z);
    this.camera.position.set(target.x - back.x, target.y + this.baseOffset.y, target.z + back.z);
    this.camera.lookAt(target.x, target.y + 1, target.z);
    this.smoothLook.set(target.x, target.y + 1, target.z);
  }

  update(
    delta: number,
    target: THREE.Vector3,
    heading: number,
    speed01: number,
    boost: boolean,
    shakeTime: number,
  ): void {
    // Position: behind boat, pulled back with speed, wider when boosting.
    const backDistance = this.baseOffset.z + speed01 * 2.6;
    const height = this.baseOffset.y + speed01 * 0.7;
    const bx = -Math.sin(heading) * backDistance;
    const bz = -Math.cos(heading) * backDistance;
    this.desired.set(target.x + bx, target.y + height, target.z + bz);
    const factor = 1 - Math.exp(-delta / 0.14);
    this.camera.position.lerp(this.desired, factor);

    // Look-ahead: further ahead at speed.
    const ahead = 6 + speed01 * 14;
    this.lookAtTarget.set(
      target.x + Math.sin(heading) * ahead,
      target.y + 2.2,
      target.z + Math.cos(heading) * ahead,
    );
    this.smoothLook.lerp(this.lookAtTarget, 1 - Math.exp(-delta / 0.18));
    this.camera.lookAt(this.smoothLook);

    // FOV punch on boost; eases back.
    if (boost) this.punchFov(0.35);
    if (this.fovPunch > 0.001) {
      this.fovPunch *= Math.exp(-delta / 0.28);
      this.camera.fov = this.baseFov + this.fovPunch;
      this.camera.updateProjectionMatrix();
    }

    // Trauma shake applied after base transform.
    this.shakeTime = shakeTime;
    this.trauma = Math.max(0, this.trauma - TRAUMA_DECAY * delta);
    if (this.trauma > 0) {
      const shake = this.trauma * this.trauma;
      const freq = this.shakeTime * 32;
      this.camera.position.x += MAX_OFFSET * shake * pseudoNoise(freq, 1);
      this.camera.position.y += MAX_OFFSET * shake * pseudoNoise(freq, 2);
      this.camera.rotation.z += MAX_ROLL * shake * pseudoNoise(freq, 3);
    }
  }
}
